import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react'
import {
  REPLY_SLASH_MAX_ITEMS,
  type ReplySlashItem,
  type SlashMenuUi,
} from '@/components/tickets/ticket-detail-page-helpers'
import type { MacroRow, ProjectTemplateRow } from '@/types/api'

type ReplyFavoritesState = {
  template_ids?: string[]
  macro_ids?: string[]
}

type UseTicketReplySlashParams = {
  replyText: string
  replyAreaRef: RefObject<HTMLTextAreaElement | null>
  templates: ProjectTemplateRow[]
  macros: MacroRow[]
  replyFavorites: ReplyFavoritesState
  setReplyText: (updater: string | ((previous: string) => string)) => void
  setReplyTemplateId: (id: string | null) => void
}

export function useTicketReplySlash({
  replyText,
  replyAreaRef,
  templates,
  macros,
  replyFavorites,
  setReplyText,
  setReplyTemplateId,
}: UseTicketReplySlashParams) {
  const pendingReplyCaretPosRef = useRef<number | null>(null)
  const slashReplaceRangeRef = useRef<{ start: number; end: number } | null>(null)
  const slashMenuRef = useRef<HTMLDivElement | null>(null)
  const [slashUI, setSlashUI] = useState<SlashMenuUi | null>(null)

  const replySlashItems: ReplySlashItem[] = useMemo(() => {
    const pinnedTemplateIds = (replyFavorites.template_ids || []).slice(0, REPLY_SLASH_MAX_ITEMS)
    const pinnedMacroIds = (replyFavorites.macro_ids || []).slice(0, REPLY_SLASH_MAX_ITEMS)
    const templatesById = new Map(templates.map((template) => [template.id, template]))
    const macrosById = new Map(macros.map((macro) => [macro.id, macro]))
    const items: ReplySlashItem[] = []
    const hasPinned = pinnedTemplateIds.length > 0 || pinnedMacroIds.length > 0
    if (hasPinned) {
      pinnedTemplateIds.forEach((id) => {
        const template = templatesById.get(id)
        if (template) {
          items.push({
            type: 'template',
            id: template.id,
            name: template.name,
            shortcut: (template.shortcut || '').replace(/^\//, ''),
            content: template.content || '',
          })
        }
      })
      pinnedMacroIds.forEach((id) => {
        const macro = macrosById.get(id)
        if (macro) {
          items.push({
            type: 'macro',
            id: macro.id,
            name: macro.name || '',
            shortcut: '',
            content: macro.content || '',
          })
        }
      })
    } else {
      const combined: ReplySlashItem[] = [
        ...templates.map((template) => ({
          type: 'template' as const,
          id: template.id,
          name: template.name,
          shortcut: (template.shortcut || '').replace(/^\//, ''),
          content: template.content || '',
        })),
        ...macros.map((macro) => ({
          type: 'macro' as const,
          id: macro.id,
          name: macro.name || '',
          shortcut: '',
          content: macro.content || '',
        })),
      ]
      combined.slice(0, REPLY_SLASH_MAX_ITEMS).forEach((item) => items.push(item))
    }
    return items
  }, [macros, replyFavorites.macro_ids, replyFavorites.template_ids, templates])

  const resetReplySlash = useCallback(() => {
    slashReplaceRangeRef.current = null
    setSlashUI(null)
  }, [])

  const requestReplyCaretAt = useCallback((pos: number) => {
    pendingReplyCaretPosRef.current = pos
  }, [])

  useLayoutEffect(() => {
    const pos = pendingReplyCaretPosRef.current
    if (pos == null) return
    const textArea = replyAreaRef.current
    if (!textArea) return
    textArea.focus()
    textArea.setSelectionRange(pos, pos)
    pendingReplyCaretPosRef.current = null
  }, [replyAreaRef, replyText])

  const pickSlashItem = useCallback((item: ReplySlashItem) => {
    const range = slashReplaceRangeRef.current
    if (!range) return
    const { start, end } = range
    const baseInsert = item.content || ''
    const insert =
      baseInsert && item.type !== 'macro' && item.type !== 'template'
        ? baseInsert
        : baseInsert && !baseInsert.endsWith('\n')
          ? `${baseInsert}\n`
          : baseInsert
    setReplyText((previous) => previous.slice(0, start) + insert + previous.slice(end))
    const newPos = start + insert.length
    slashReplaceRangeRef.current = null
    setSlashUI(null)
    setReplyTemplateId(item.type === 'template' ? item.id : null)
    requestReplyCaretAt(newPos)
  }, [requestReplyCaretAt, setReplyTemplateId, setReplyText])

  const processSlashInReply = useCallback(
    (value: string, selectionStart: number) => {
      const before = value.slice(0, selectionStart)
      const slashIdx = before.lastIndexOf('/')
      if (slashIdx === -1 || before.slice(slashIdx).includes('\n')) {
        resetReplySlash()
        return
      }
      const query = value.slice(slashIdx + 1, selectionStart)
      slashReplaceRangeRef.current = { start: slashIdx, end: selectionStart }
      const q = query.toLowerCase()
      const filtered =
        query === ''
          ? replySlashItems
          : replySlashItems.filter((item) => {
              const name = (item.name || '').toLowerCase()
              const shortcut = (item.shortcut || '').toLowerCase()
              return name.includes(q) || shortcut.includes(q)
            })
      const textArea = replyAreaRef.current
      if (!textArea) return
      const rect = textArea.getBoundingClientRect()
      const bottom = window.innerHeight - rect.top + 6
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - 236))
      setSlashUI({ filtered, selectedIndex: 0, left, bottom })
    },
    [replyAreaRef, replySlashItems, resetReplySlash],
  )

  const handleReplyKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!slashUI) return
      const options = slashUI.filtered
      if (options.length === 0) {
        if (event.key === 'Escape') {
          event.preventDefault()
          resetReplySlash()
        }
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSlashUI((state) =>
          state ? { ...state, selectedIndex: Math.min(state.selectedIndex + 1, options.length - 1) } : state,
        )
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSlashUI((state) => (state ? { ...state, selectedIndex: Math.max(state.selectedIndex - 1, 0) } : state))
        return
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        const item = options[slashUI.selectedIndex]
        if (item) pickSlashItem(item)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        resetReplySlash()
      }
    },
    [pickSlashItem, resetReplySlash, slashUI],
  )

  useEffect(() => {
    if (!slashUI) return
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node
      if (slashMenuRef.current?.contains(target)) return
      if (replyAreaRef.current?.contains(target)) return
      resetReplySlash()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [replyAreaRef, resetReplySlash, slashUI])

  return {
    handleReplyKeyDown,
    pickSlashItem,
    processSlashInReply,
    requestReplyCaretAt,
    resetReplySlash,
    slashMenuRef,
    slashUI,
  }
}
